import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Skeleton,
  StatTile,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { formatDate, formatNumber } from '../../components/admin/labels';
import { IconAlert, IconCases, IconClock, IconSearch } from '../../components/admin/icons';
import { TrendChart, UpgradeGate, WindowPicker } from '../../components/courier/ui';
import { useCourierSession } from '../../components/courier/CourierLayout';

/** Chart-series colours, in the order issues come back ranked. */
const BARS = ['bg-data-red', 'bg-data-orange', 'bg-data-blue', 'bg-data-purple', 'bg-data-grey'];

const ChangeBadge = ({ pct }) => {
  // Against a zero baseline there is no percentage to state, so say so rather
  // than printing a misleading 0% or 100%. More reported issues is not good
  // news, so up is danger and down is success.
  if (pct == null) return <span className="text-xs text-ink-500">no comparison yet</span>;
  const up = pct > 0;
  return (
    <span className={`text-sm font-700 ${up ? 'text-danger-600' : 'text-success-600'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
};

const CourierDashboard = () => {
  const toast = useToast();
  const { me, refreshMe, fullAccess } = useCourierSession() || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (windowDays) => {
      setLoading(true);
      try {
        const [dashRes, trendRes] = await Promise.all([
          api.get(`/api/courier-portal/dashboard?days=${windowDays}`),
          api.get(`/api/courier-portal/trends?days=${windowDays}`)
        ]);
        setData(dashRes.data);
        setTrends(trendRes.data);
      } catch (error) {
        toast('Could not load your dashboard.', { tone: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    load(days);
  }, [load, days]);

  // Stripe sends the browser back here the moment checkout completes, which is
  // routinely before the webhook lands — so the plan is applied from the
  // session id directly rather than waiting on it. Ref-guarded against React
  // 18 StrictMode's double effect in development.
  const confirmedRef = useRef(false);
  const sessionId = searchParams.get('session_id');
  useEffect(() => {
    if (!sessionId || confirmedRef.current) return;
    confirmedRef.current = true;
    setSearchParams({}, { replace: true });

    api
      .post('/api/couriers/confirm-plan-checkout', { sessionId })
      .then(() => {
        toast("You're on Intelligence — welcome aboard.", { tone: 'success' });
        refreshMe?.();
        load(days);
      })
      .catch(() => {
        // Paid either way: the webhook applies it even if this call failed.
        toast('Payment received. Your plan will be active shortly.', { tone: 'info' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const account = me?.account;
  const intelligencePrice = me?.pricing?.courierPlanPrices?.intelligence?.price ?? null;
  const intelligenceFeatures = me?.pricing?.courierPlanPrices?.intelligence?.features || [];

  if (loading && !data) {
    return (
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((k) => (
            <Skeleton key={k} className="h-28 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <WindowPicker days={days} onChange={setDays} onRefresh={() => load(days)} loading={loading} />
      </div>

      {/* Not linked to a carrier yet — the honest state, rather than an empty
          dashboard that looks like "no issues reported". */}
      {data && !data.linked && (
        <Card>
          <EmptyState
            icon={IconAlert}
            title="Your account is still being set up"
            description="We haven't linked your account to a carrier yet, so there's nothing to show. The ParcelBuddy team will finish this shortly — get in touch if it's urgent."
            action={
              <Button as={Link} to="/contact?topic=courier" variant="secondary" size="sm">
                Contact us
              </Button>
            }
          />
        </Card>
      )}

      {/* Pilot spent: the figures stop and the plan takes their place. Not an
          empty dashboard — they had real data a moment ago and are owed an
          explanation of where it went and what brings it back. */}
      {data?.pilotSpent && (
        <UpgradeGate
          title="Your free pilot has finished"
          description={`You've seen the ${data.pilotAllowance ?? ''} free cases your pilot covers. Intelligence turns the dashboard back on in full.`}
          features={intelligenceFeatures}
          price={intelligencePrice}
        />
      )}

      {data?.linked && !data.pilotSpent && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatTile
              label="Issues reported"
              value={formatNumber(data.totals.current)}
              hint={`in the last ${data.windowDays} days`}
              icon={IconCases}
            />
            <StatTile
              label={`vs previous ${data.windowDays} days`}
              value={formatNumber(data.totals.previous)}
              hint="previous period total"
              icon={IconClock}
            />
            <StatTile
              label="Top issue"
              value={data.issues[0]?.label || '—'}
              hint={data.issues[0] ? `${data.issues[0].pct}% of reports` : 'No reports in this period'}
              icon={IconSearch}
            />
          </div>

          <Card>
            <CardHeader
              title="Reports over time"
              description={`Issues raised against ${data.courier} each day, and how this period compares.`}
              actions={<ChangeBadge pct={data.totals.changePct} />}
            />
            <div className="px-5 pb-5 pt-4">
              <TrendChart points={trends?.days || []} />
            </div>
          </Card>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card>
              <CardHeader
                title="What customers are reporting"
                description={`Share of issues in the last ${data.windowDays} days.`}
              />
              <div className="px-5 pb-5">
                {data.issues.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-500">
                    No issues reported in this period.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {data.issues.map((issue, i) => (
                      <li key={issue.key}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-ink-700">{issue.label}</span>
                          <span className="font-600 tabular-nums text-ink-900">
                            {formatNumber(issue.count)}{' '}
                            <span className="font-400 text-ink-500">({issue.pct}%)</span>
                          </span>
                        </div>
                        <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-ink-100">
                          <div
                            className={`h-full rounded-full ${BARS[i % BARS.length]}`}
                            style={{ width: `${issue.pct}%` }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card>

            {fullAccess ? (
              <Card>
                <CardHeader
                  title="Top retailers"
                  description="Where your reported issues cluster."
                  actions={
                    <Button as={Link} to="/courier/merchants" variant="ghost" size="sm">
                      See all
                    </Button>
                  }
                />
                <div className="px-5 pb-5">
                  {data.merchants.length === 0 ? (
                    <p className="py-6 text-center text-sm text-ink-500">Nothing in this period.</p>
                  ) : (
                    <ul className="space-y-2">
                      {data.merchants.map((m) => (
                        <li
                          key={m.name}
                          className="flex items-center justify-between rounded-lg bg-ink-50 px-3.5 py-2.5"
                        >
                          <span className="truncate text-sm font-600 text-ink-900">{m.name}</span>
                          <span className="text-sm tabular-nums text-ink-600">
                            {formatNumber(m.count)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Card>
            ) : (
              <UpgradeGate
                title="Retailers, hotspots and the reports themselves"
                description="The pilot shows the headline figures. Intelligence adds the detail behind them."
                features={intelligenceFeatures}
                price={intelligencePrice}
              />
            )}
          </div>

          {fullAccess && (
            <Card>
              <CardHeader
                title="Recent reports"
                description="The latest issues naming your network."
                actions={
                  <Button as={Link} to="/courier/cases" variant="ghost" size="sm">
                    See all
                  </Button>
                }
              />
              {data.recent.length === 0 ? (
                <p className="px-5 pb-6 text-center text-sm text-ink-500">Nothing yet.</p>
              ) : (
                <TableWrap>
                  <thead>
                    <tr>
                      <Th>Case</Th>
                      <Th>Issue</Th>
                      <Th>Retailer</Th>
                      <Th>Reported</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((c) => (
                      <Tr key={c.caseId}>
                        <Td className="font-600 text-ink-900">{c.caseId}</Td>
                        <Td className="text-ink-700">{c.issueLabel}</Td>
                        <Td className="text-ink-700">{c.merchantName || '—'}</Td>
                        <Td className="whitespace-nowrap text-ink-500">{formatDate(c.submittedAt)}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
            </Card>
          )}

          {!fullAccess && account?.freeCaseAllowance != null && (
            <p className="text-center text-xs text-ink-500">
              Pilot: {formatNumber(account.pilotCasesUsed || 0)} of{' '}
              {formatNumber(account.freeCaseAllowance)} free cases used.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default CourierDashboard;
